'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RequestResetPasswordForm } from '@components/RequestResetPasswordForm';
import { SuccessMessage } from '@components/SuccessMessageForm';

/**
 * @description Request Reset Password Form component
 * Provides a form where users can request a password reset link by entering email.
 * On submission, it sends a request to the backend API to email the reset link.
 */

interface ResetPasswordFormData {
  email: string;
}

interface RequestResetPasswordProps {
  activate: boolean;
}

export function RequestResetPassword({ activate = false }: RequestResetPasswordProps) {
  const { t } = useTranslation('common');
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * @description Sends a request to the backend to generate a password reset link
   * @param email - The user's email address.
   */
  const requestPasswordReset = async (email: string) => {
    // A custom reset url - the link in the email will lead to
    const reset_url = activate
      ? `${process.env.NEXT_PUBLIC_BASE_URL}/activate`
      : `${process.env.NEXT_PUBLIC_BASE_URL}/reset-password`;

    const response = await fetch('/api/request-reset-password', {
      method: 'POST',
      body: JSON.stringify({ email, reset_url }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Password reset failed');
    }

    return data.message || 'Password reset link sent';
  };

  /**
   * @description Handles the form submission to request a password reset / activate account.
   * @param formData - The form data containing the email.
   */
  const handleFormSubmit = async (formData: ResetPasswordFormData) => {
    try {
      const message = await requestPasswordReset(formData.email);
      setSuccess(message);
      setError(null);
    } catch (error: unknown) {
      setSuccess(null);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError(String(error));
      }
    }
  };

  // Renders a success message after a password reset request.
  const renderSuccessMessage = (
    <SuccessMessage
      label={t('reset_password.successLabel')}
      header={t('reset_password.successTitle')}
      paragraph={
        activate ? t('request_activate.successDescription') : t('reset_password.successDescription')
      }
      buttonText={t('backToLogin')}
      buttonVariant="primary"
      link="/login"
    />
  );

  return (
    <>
      {success ? (
        renderSuccessMessage
      ) : (
        <>
          <RequestResetPasswordForm
            onSubmit={handleFormSubmit}
            hasError={!!error}
            activate={activate}
          />
        </>
      )}
    </>
  );
}
